import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { fetchAuthSession, getCurrentUser, signOut } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';

// Define a type for the user from AWS Amplify
interface AmplifyUser {
  username: string;
  userId: string;
  signInDetails?: {
    loginId?: string;
  };
  // Add any other properties that might be on the user object
  [key: string]: unknown;
}

interface AuthContextType {
  user: AmplifyUser | null;
  setUser: React.Dispatch<React.SetStateAction<AmplifyUser | null>>;
  loading: boolean;
}

interface AuthProviderProps {
  children: ReactNode;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AmplifyUser | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const clearCachedAuth = () => {
    try {
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith('CognitoIdentityServiceProvider') || key.startsWith('amplify')) {
          localStorage.removeItem(key);
        }
      });
      Object.keys(sessionStorage).forEach((key) => {
        if (key.startsWith('CognitoIdentityServiceProvider') || key.startsWith('amplify')) {
          sessionStorage.removeItem(key);
        }
      });
    } catch {
      // Ignore storage access errors.
    }
  };

  useEffect(() => {
    const checkUser = async () => {
      try {
        console.log('AuthContext: checking current user');
        const userData = await getCurrentUser();
        await fetchAuthSession(); // Fetch session but don't store it
        console.log('AuthContext: user loaded', { userId: userData.userId, username: userData.username });
        setUser(userData as AmplifyUser);
      } catch (err) {
        const message = err instanceof Error ? err.message : '';
        if (message.includes('UserNotFoundException') || message.includes('User does not exist')) {
          console.log('AuthContext: stale session detected, signing out');
          try {
            await signOut();
          } catch {
            // Ignore sign out errors.
          }
        }
        clearCachedAuth();
        console.log('AuthContext: no current user', err);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkUser();

    const listener = ({ payload }: { payload: { event: string; data?: unknown } }) => {
      switch (payload.event) {
        case 'signedIn':
          console.log('AuthContext: Hub signedIn');
          checkUser();
          break;
        case 'signedOut':
          console.log('AuthContext: Hub signedOut');
          clearCachedAuth();
          setUser(null);
          break;
        default:
          break;
      }
    };

    // In AWS Amplify v6, Hub.listen returns a cleanup function directly
    const unsubscribe = Hub.listen('auth', listener);
    return () => {
      unsubscribe();
    };
  }, []);

  return <AuthContext.Provider value={{ user, setUser, loading }}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
